import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const OFFICIAL_MODELS = ['Fluora Floor', 'Fluora Mini', 'Monos'];

interface ModelSelectorProps {
  apiBaseUrl: string;
  selectedModels: string[];
  onSelectionChange: (models: string[]) => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ 
  apiBaseUrl, 
  selectedModels, 
  onSelectionChange 
}) => {
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/models`, {
          withCredentials: true,
        });
        const fetched = (response.data.models || [])
          .map((model: { name?: string }) => String(model?.name || '').trim())
          .filter(Boolean);
        const extras = fetched.filter((name: string) => !OFFICIAL_MODELS.includes(name)).sort();
        const uniqueModels = [...OFFICIAL_MODELS, ...extras];

        setAvailableModels(uniqueModels);
        if (selectedModels.length === 0) {
          onSelectionChange(uniqueModels);
        }
        setError(null);
      } catch (error) {
        console.error('Error fetching device models:', error);
        setAvailableModels(OFFICIAL_MODELS);
        if (selectedModels.length === 0) {
          onSelectionChange(OFFICIAL_MODELS);
        }
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [apiBaseUrl]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleModelToggle = (model: string) => {
    const newSelection = selectedModels.includes(model)
      ? selectedModels.filter(m => m !== model)
      : [...selectedModels, model];
    
    onSelectionChange(newSelection);
  };

  const selectAll = () => {
    onSelectionChange([...availableModels]);
  };

  const deselectAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className="model-selector mb-4" ref={dropdownRef}>
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Device Models</h5>
        <div className="dropdown">
          <button 
            className="btn btn-outline-secondary dropdown-toggle" 
            type="button"
            onClick={toggleDropdown}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filter Models
            <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
          </button>
          {dropdownOpen && (
            <div className="dropdown-menu show mt-1 p-2" style={{ minWidth: '250px' }}>
              <div className="d-flex justify-content-between mb-2 px-2">
                <button className="btn btn-sm btn-link" onClick={selectAll}>Select All</button>
                <button className="btn btn-sm btn-link" onClick={deselectAll}>Deselect All</button>
              </div>
              <div className="dropdown-divider"></div>
              {loading ? (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="text-danger px-2 py-1">{error}</div>
              ) : (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {availableModels.map(model => (
                    <div className="dropdown-item" key={model}>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`model-${model}`}
                          checked={selectedModels.includes(model)}
                          onChange={() => handleModelToggle(model)}
                        />
                        <label className="form-check-label" htmlFor={`model-${model}`}>
                          {model}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {selectedModels.length > 0 && (
        <div className="mt-2 d-flex flex-wrap gap-1">
          {selectedModels.map(model => (
            <span key={model} className="badge bg-primary me-1 mb-1">
              {model}
              <button 
                type="button" 
                className="btn-close btn-close-white ms-1" 
                aria-label="Remove" 
                style={{ fontSize: '0.5rem' }}
                onClick={() => handleModelToggle(model)}
              ></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;